import React, { useCallback } from "react";
import { TaxIdentity as TaxIdentityApi, Users as UsersApi } from '../../../api/services/next.js';
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";
import { TrashButton, EditButton, RegisterButton, BookmarkButton } from '../../../components/shared/icons.jsx';
import useBoolean from '../../../hooks/useBoolean.jsx';
import EditTaxIdentityPopup from '../popups/EditTaxIdentityPopup.jsx';
import { useTextResources } from "../../../language.js";
import { useConfirm } from "../../../hooks/useConfirm.jsx";
import { useLoader } from '../../../hooks/useLoader';
import { onError } from '../../../components/shared/messages.jsx';
import { digitalCertificateValid } from '../utils.js';
import { TaxIdentityFeatureEnum } from '../../../components/shared/constants.js';

const ActionsColumn = ({ row, column: { saveTaxIdentity, refreshData } }) => {
    const text = useTextResources();
    const loader = useLoader();

    const confirm = useConfirm();
    const {
        value: isEditPopupOpen,
        setTrue: openEditPopup,
        setFalse: closeEditPopup
    } = useBoolean(false);

    const handleRemove = useCallback(() => {
        saveTaxIdentity({
            ...row,
            IsActive: false,
        });
    }, [row, saveTaxIdentity]);

    const handleTaxAuthorityRegisterUrlRegister = useCallback(async () => {
        loader(true);
        try {
            const response = await TaxIdentityApi.GetTaxAuthorityRegisterUrl({ Id: row.Id });
            window.open(response.RegisterUrl, '_blank');
        } catch (error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [row.Id, loader]);

    const handleCreateDigitalCertificate = useCallback(async () => {
        loader(true);
        try {
            await UsersApi.CreateDigitalCertificate(row.UserId);
            refreshData();
        } catch (error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [row.UserId, loader]);

    const IsDigitalCertificateFeature = row.Type == TaxIdentityFeatureEnum.DigitalInvoice;

    const handleRegister = IsDigitalCertificateFeature ? handleCreateDigitalCertificate : handleTaxAuthorityRegisterUrlRegister;
    let certificateActive = IsDigitalCertificateFeature && digitalCertificateValid(row.TokenExpirationDate);

    const confirmRemove = useCallback(async () => {
        await confirm({
            header: `${text('Delete')} ${text('TaxIdentity')}`,
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: handleRemove,
        });
    }, [confirm, text, handleRemove]);

    if (!row.IsActive) {
        return null;
    }

    return (
        <div>
            <Button.Group icon>
                {certificateActive ?
                    <BookmarkButton content={'Done'} onClick={() => { }} style={{ marginRight: '10px', color: '#8BC34A' }} /> :
                    !IsDigitalCertificateFeature ?
                        <RegisterButton onClick={handleRegister} style={{ marginRight: '10px' }} /> :
                        <BookmarkButton onClick={handleRegister} style={{ marginRight: '10px' }} />
                }
                <EditButton onClick={openEditPopup} style={{ marginRight: '10px' }} />
                <TrashButton onClick={confirmRemove} />
            </Button.Group>
            <EditTaxIdentityPopup
                open={isEditPopupOpen}
                taxIdentity={row}
                save={saveTaxIdentity}
                onClose={closeEditPopup}
            />
        </div>
    );
};

ActionsColumn.propTypes = {
    row: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        IsActive: PropTypes.bool.isRequired,
        TokenExpirationDate: PropTypes.object,
        UserId: PropTypes.number,
        Type: PropTypes.number,
    }).isRequired,
    column: PropTypes.shape({
        saveTaxIdentity: PropTypes.func.isRequired,
        refreshData: PropTypes.func.isRequired
    }).isRequired,
};


export default ActionsColumn;